import { type AccountOpeningTypeSelector, type AccountType, type CatalogOption, type RequiredRole, type RequiredRoleKind } from '../../../models/AccountOpening'
import { titleCase } from './schemaValues'
import { type ContactOption, type HolderNameSource, type WizardHolder } from './types'

interface HolderSlot {
  roleCode: string
  label: string
  kind: RequiredRoleKind
}

const roleLabel = (roleCode: string): string => titleCase(roleCode)

export const roleSlotLabel = (required: RequiredRole): string => required.label || roleLabel(required.role)

export const roleLabelIn = (accountTypes: AccountType[], roleCode: string): string => {
  const named = accountTypes.flatMap((type) => type.requiredRoles).find((required) => required.role === roleCode && !!required.label)
  return named?.label ?? roleLabel(roleCode)
}

const dedupedOptions = <T>(items: T[], pick: (item: T) => CatalogOption): CatalogOption[] => {
  const byValue = new Map<string, CatalogOption>()
  items.forEach((item) => {
    const option = pick(item)
    if (option.value && !byValue.has(option.value)) {
      byValue.set(option.value, option)
    }
  })
  return [...byValue.values()]
}

type CatalogEntry = AccountType & Record<string, unknown>

const readString = (type: AccountType, key: string): string => {
  const value = (type as CatalogEntry)[key]
  return typeof value === 'string' ? value : ''
}

export const axisValue = (type: AccountType, field: string): string => readString(type, field)

export const axisLabel = (type: AccountType, field: string): string => readString(type, `${field}Label`) || readString(type, field)

export const unresolvedAxes = (accountTypes: AccountType[], selectors: AccountOpeningTypeSelector[]): string[] =>
  selectors.filter((selector) => accountTypes.every((type) => !axisValue(type, selector.field))).map((selector) => selector.field)

export const axisOptions = (
  accountTypes: AccountType[],
  selectors: AccountOpeningTypeSelector[],
  selection: Record<string, string>,
  axisIndex: number,
): CatalogOption[] => {
  const field = selectors[axisIndex]?.field
  if (!field) {
    return []
  }
  const reachable = accountTypes.filter((type) =>
    selectors.slice(0, axisIndex).every((selector) => axisValue(type, selector.field) === (selection[selector.field] ?? '')),
  )
  return dedupedOptions(reachable, (type) => ({ value: axisValue(type, field), label: axisLabel(type, field) }))
}

export const resolveTypeId = (accountTypes: AccountType[], selectors: AccountOpeningTypeSelector[], selection: Record<string, string>): string => {
  if (selectors.length === 0) {
    return ''
  }
  return accountTypes.find((type) => selectors.every((selector) => axisValue(type, selector.field) === (selection[selector.field] ?? '')))?.id ?? ''
}

interface SelectionDisplay {
  field: string
  label: string
  value: string
}

export const selectionDisplay = (accountTypes: AccountType[], selectors: AccountOpeningTypeSelector[], selection: Record<string, string>): SelectionDisplay[] =>
  selectors.map((selector, axisIndex) => {
    const code = selection[selector.field] ?? ''
    const named = accountTypes.find((type) => selectors.slice(0, axisIndex + 1).every((axis) => axisValue(type, axis.field) === (selection[axis.field] ?? '')))
    return { field: selector.field, label: selector.label, value: named ? axisLabel(named, selector.field) : code }
  })

export const findAccountType = (accountTypes: AccountType[], typeId: string): AccountType | undefined => accountTypes.find((type) => type.id === typeId)

export const requiredRolesFor = (accountTypes: AccountType[], typeId: string): RequiredRole[] => findAccountType(accountTypes, typeId)?.requiredRoles ?? []

export const holderSlots = (accountTypes: AccountType[], typeId: string): HolderSlot[] =>
  requiredRolesFor(accountTypes, typeId).flatMap((required) => {
    const count = Math.max(0, required.min)
    const label = roleSlotLabel(required)
    return Array.from({ length: count }, (_, index) => ({
      roleCode: required.role,
      label: count > 1 ? `${label} ${index + 1}` : label,
      kind: required.kind ?? 'individual',
    }))
  })

export const roleOptions = (accountTypes: AccountType[], typeId: string): CatalogOption[] =>
  dedupedOptions(requiredRolesFor(accountTypes, typeId), (required) => ({ value: required.role, label: roleSlotLabel(required) }))

export const roleKindIn = (accountTypes: AccountType[], roleCode: string): RequiredRoleKind =>
  accountTypes.flatMap((type) => type.requiredRoles).find((required) => required.role === roleCode)?.kind ?? 'individual'

const roleCounts = (holders: WizardHolder[], skipIndex?: number): Map<string, number> => {
  const counts = new Map<string, number>()
  holders.forEach((holder, index) => {
    if (index !== skipIndex) {
      counts.set(holder.roleCode, (counts.get(holder.roleCode) ?? 0) + 1)
    }
  })
  return counts
}

export const rolesWithHeadroom = (accountTypes: AccountType[], typeId: string, holders: WizardHolder[], skipIndex?: number): CatalogOption[] => {
  const counts = roleCounts(holders, skipIndex)
  const open = requiredRolesFor(accountTypes, typeId).filter((required) => required.max === undefined || (counts.get(required.role) ?? 0) < required.max)
  return dedupedOptions(open, (required) => ({ value: required.role, label: roleSlotLabel(required) }))
}

export const relationshipOptionsFor = (accountTypes: AccountType[], typeId: string): CatalogOption[] =>
  findAccountType(accountTypes, typeId)?.relationshipOptions ?? []

export const asksRelationshipAt = (accountTypes: AccountType[], typeId: string, holderIndex: number): boolean => {
  if (relationshipOptionsFor(accountTypes, typeId).length === 0) {
    return false
  }
  return (findAccountType(accountTypes, typeId)?.relationshipAppliesTo ?? 'nonPrimaryHolders') === 'allHolders' || holderIndex > 0
}

export const defaultNameSource = (contactOptions: ContactOption[]): HolderNameSource => (contactOptions.length > 0 ? null : 'manual')
